import React from 'react';
import { motion } from 'framer-motion';
import type { Variants } from 'framer-motion';

const EASE = [0.16, 1, 0.3, 1] as const;

interface RevealProps {
    children: React.ReactNode;
    className?: string;
    delay?: number;
    y?: number;
    as?: 'div' | 'section' | 'span' | 'li';
    once?: boolean;
}

/** Scroll-triggered fade-and-rise. Respects prefers-reduced-motion via framer-motion. */
export const Reveal: React.FC<RevealProps> = ({
    children,
    className,
    delay = 0,
    y = 24,
    once = true,
}) => (
    <motion.div
        className={className}
        initial={{ opacity: 0, y }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once, margin: '-80px' }}
        transition={{ duration: 0.7, delay, ease: EASE }}
    >
        {children}
    </motion.div>
);

/** Container that staggers its <RevealItem> children as they enter the viewport. */
export const RevealGroup: React.FC<{
    children: React.ReactNode;
    className?: string;
    stagger?: number;
}> = ({ children, className, stagger = 0.08 }) => {
    const container: Variants = {
        hidden: {},
        show: { transition: { staggerChildren: stagger } },
    };
    return (
        <motion.div
            className={className}
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-60px' }}
        >
            {children}
        </motion.div>
    );
};

export const itemVariants: Variants = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

export const RevealItem: React.FC<{ children: React.ReactNode; className?: string }> = ({
    children,
    className,
}) => (
    <motion.div className={className} variants={itemVariants}>
        {children}
    </motion.div>
);

export default Reveal;
